import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, TextField, Divider } from "@mui/material"
import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import FormControlLabel from "@mui/material/FormControlLabel"
import Checkbox from "@mui/material/Checkbox"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import { UTextField } from "@/components/index"
import { UText } from "@/components/index"
import { wasmModule } from "@/gluelayer"
import {
  EPrintModel,
  IPrintSettingJsonObj,
} from "@/core/app/slices/user/user.type"
export const PrintOthers: FC<{
  exportModel: EPrintModel
  jsonData: IPrintSettingJsonObj
  updateJsonData: (data: IPrintSettingJsonObj) => void
}> = ({ exportModel, jsonData, updateJsonData }) => {
  const dispatch = useAppDispatch()

  const [trimValue, setTrimValue] = useState<number>(null)
  // // hollow model
  const [hollowModel, setHollowModel] = useState<number>(0)
  const handleHollowModel = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateJsonData({ GenerateShell: +(event.target as HTMLInputElement).value })
  }
  // Drain hole
  const [drainHole, setDrainHole] = useState<number>(0)
  const handleDrainHole = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateJsonData({ DrainHole: +(event.target as HTMLInputElement).value })
  }
  // Thickness
  const [thickness, setThickness] = useState<number | string>(0)
  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    min: number,
    max: number,
    setVal: (arg: string | number) => void,
  ) => {
    let value = e.target.value
      .replace(/[^0-9.]/g, "") // 移除非数字和小数点
      .replace(/(\..*)\./g, "$1") // 禁止多个小数点
      .replace(/^\./, `${min}.`) // 处理单独的小数点开头
      .replace(/(\.\d{1})\d+$/, "$1") // 限制一位小数

    // 格式验证和范围修正
    if (/^\d*\.?\d*$/.test(value) && value !== "") {
      const numValue = parseFloat(value)
      if (numValue < min) {
        value = `${min}.0`
      } else if (numValue > max) {
        value = `${max}.0`
      }
    }
    setVal(value)
  }
  const handleBlurThick = () => {
    if (thickness) {
      const num = parseFloat(thickness)
      updateJsonData({ ShellThickness: num })
    } else {
      updateJsonData({ ShellThickness: jsonData.ShellThickness })
    }
  }
  const handleBlurHeight = () => {
    if (heightModel) {
      const num = parseFloat(heightModel)
      updateJsonData({ HeightOfWall: num })
    } else {
      updateJsonData({ HeightOfWall: jsonData.HeightOfWall })
    }
  }
  // Print direction
  const [printDirection, setPrintDirection] = useState<number>(1)
  const handlePrintDirection = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrintDirection(+(event.target as HTMLInputElement).value)
    updateJsonData({
      PrintDirection: +(event.target as HTMLInputElement).value,
    })
  }

  // Height model
  const [heightModel, setHeightModel] = useState<number | string>()
  // DPA offeset
  const [offesetDPA, setOffesetDPA] = useState<number | string>(0)
  const handleBlurOffeset = () => {
    if (offesetDPA) {
      const num = parseFloat(offesetDPA)
      // setOffesetDPA(num % 1 === 0 ? num.toString() : num.toFixed(2)) // 失焦格式化
      updateJsonData({ DPAOffset: num })
      wasmModule.moduleManager.GetModulePrintSelection().SetDPAOffset(num)
    }
  }
  const handleOffesetChange = (e) => {
    // 1. 清除非法字符（保留数字、负号和小数点）
    let value = e.target.value
      .replace(/[^\d.-]/g, "")
      .replace(/(?!^)-/g, "") // 只允许开头有负号
      .replace(/\.+/g, ".") // 合并多个小数点

    // 2. 处理小数点位置和位数
    const parts = value.split(".")
    if (parts.length > 1) {
      value = parts[0] + "." + parts[1].slice(0, 2) // 限制两位小数
    }

    // 3. 验证数值范围
    if (value && value !== "-") {
      const numValue = parseFloat(value)
      if (numValue < -0.4 || numValue > 0.4) {
        value = value.substring(0, value.length - 1)
      }
    }

    setOffesetDPA(value)
  }
  // DPA thinckness
  const [thicknessDPA, setThicknessDPA] = useState<number | string>(0.5)
  const handleThicknessChange = (e) => {
    let value = e.target.value
      .replace(/[^\d.]/g, "")
      .replace(/^\./, "")
      .replace(/(\.\d{0,2}).*/, "$1")

    // 数值范围验证
    if (
      value &&
      value !== "0" &&
      value !== "." &&
      value !== "0." &&
      value !== "0.3"
    ) {
      const num = parseFloat(value)
      if (num <= 0.3 || num > 1.5) {
        value = value.slice(0, -1)
      }
    }
    setThicknessDPA(value)
  }
  const handleBlurThickness = () => {
    if (thicknessDPA) {
      let num = parseFloat(thicknessDPA)
      if (num <= 0.3) {
        num = 0.31
      }
      // setThicknessDPA(num % 1 === 0 ? num.toString() : num.toFixed(2)) // 失焦格式化
      updateJsonData({ DPAthickness: num })
      wasmModule.moduleManager.GetModulePrintSelection().SetDPAThickness(num)
    }
  }
  // pass 0 / 0.5 to offest and thinckness for wasm
  useEffect(() => {
    try {
      if(!wasmModule.moduleManager
        .GetModulePrintSelection())
        return;
      wasmModule.moduleManager
        .GetModulePrintSelection()
        .SetDPAOffset(offesetDPA)
      wasmModule.moduleManager
        .GetModulePrintSelection()
        .SetDPAThickness(thicknessDPA)
    } catch (error) {
      console.error(error)
    }
  }, [])
  useEffect(() => {
    if (!jsonData) {
      return
    }

    setHollowModel(jsonData.GenerateShell)
    setDrainHole(jsonData.DrainHole)
    setThickness(jsonData.ShellThickness)
    setPrintDirection(jsonData.PrintDirection)
    setHeightModel(jsonData.HeightOfWall)
    setTrimValue(jsonData.UseuContourMachine)

    setOffesetDPA(jsonData.DPAOffset)
    setThicknessDPA(jsonData.DPAthickness)
    const PrintModule = wasmModule.moduleManager.GetModulePrintSelection()
    if (PrintModule) {
      try {
        if (jsonData.DPAOffset != offesetDPA)
          PrintModule.SetDPAOffset(jsonData.DPAOffset)
        if (jsonData.DPAthickness != thicknessDPA)
          PrintModule.SetDPAThickness(jsonData.DPAthickness)
      } catch (error) {
        console.log(error)
      }
    }
  }, [jsonData])
  // hollow\ drain\ thickness\ print direction\ height\ to wasm
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (!printMoudle) return
      if (hollowModel === 1) {
        if (jsonData.SelectPositionLabel === "Bottom") {
          // setToothPosition("Wall")
          updateJsonData({
            SelectPositionLabel: "Wall",
          })
        }
        printMoudle.SetHollowModel(true)
      }
      if (hollowModel === 0) {
        printMoudle.SetHollowModel(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [hollowModel])
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (!printMoudle) return
      if (drainHole === 1) {
        printMoudle.SetDrainHole(true)
      }
      if (drainHole === 0) {
        printMoudle.SetDrainHole(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [drainHole])
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (!printMoudle) return
      if (printDirection === 1) {
        printMoudle.SetPrintDirection(true)
      }
      if (printDirection === 0) {
        printMoudle.SetPrintDirection(false)
      }
    } catch (error) {
      console.error(error)
    }
  }, [printDirection])
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (!printMoudle) return
      if (
        thickness !== "" &&
        typeof +thickness === "number" &&
        !isNaN(+thickness)
      ) {
        printMoudle.SetShellThickness(+thickness)
      }
    } catch (error) {
      console.error(error)
    }
  }, [thickness])
  useEffect(() => {
    try {
      const printMoudle = wasmModule.moduleManager.GetModulePrintSelection()
      if (
        heightModel !== "" &&
        typeof +heightModel === "number" &&
        !isNaN(+heightModel)
      ) {
        printMoudle.SetHeightOfModelBase(+heightModel)
      }
    } catch (error) {
      console.error(error)
    }
  }, [heightModel])
  // useEffect(() => {
  //   if (+trimValue === 2 || +trimValue === 3) {
  //     if (+heightModel < 5) {
  //       setHeightModel(5)
  //       updateJsonData({ HeightOfWall: 5 })
  //     }
  //   }
  // }, [trimValue])
  const styleObj = {
    height: "80px",
    borderRadius: "6px",
    // background: " #ECEFF1",
    // padding: "20px 10px",
    // marginRight: "20px",
    flexDirection: "row",
  }
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          id="other1"
          sx={{
            display: exportModel === EPrintModel.DPAModel ? "none" : "flex",
            gap: "24px",
            ...styleObj,
          }}
        >
          <FormControl sx={{ flex: 1 }}>
            <FormLabel id="hollow">{`Hollow the model`}</FormLabel>
            <RadioGroup
              row
              sx={{
                marginLeft: "8px",
                gap: "40px",
              }}
              aria-labelledby="hollow"
              name="radio-buttons-group"
              value={hollowModel??0}
              onChange={handleHollowModel}
            >
              <FormControlLabel value={1} control={<Radio />} label="Yes" />
              <FormControlLabel value={0} control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ flex: 1 }} disabled={!hollowModel}>
            <FormLabel>{`Drain hole`}</FormLabel>
            <RadioGroup
              row
              name="radio-buttons-group"
              sx={{
                marginLeft: "8px",
                gap: "40px",
              }}
              value={drainHole??0}
              onChange={handleDrainHole}
            >
              <FormControlLabel value={1} control={<Radio />} label="Yes" />
              <FormControlLabel value={0} control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
          <FormControl sx={{ flex: 1 }} disabled={!hollowModel}>
            <FormLabel
              sx={{ marginBottom: "4px" }}
            >{`Thickness of model(2~3mm)`}</FormLabel>
            <TextField
              disabled={!hollowModel}
              sx={{
                width: "100px",
                // marginTop: "6px",
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                  height: "24px",
                },
              }}
              value={thickness??""}
              onChange={(e) => {
                handleInput(e, 2, 3, setThickness)
              }}
              onBlur={handleBlurThick}
            />
          </FormControl>
        </Box>
        <Divider
          sx={{
            margin: "16px 0",
            display: exportModel === EPrintModel.DPAModel ? "none" : "flex",
          }}
        />
        <Box
          id="other2"
          sx={{
            display: exportModel === EPrintModel.DPAModel ? "none" : "flex",

            ...styleObj,
          }}
        >
          <FormControl sx={{ marginBottom: "12px" }}>
            <FormLabel id="printDirection">{`Print direction`}</FormLabel>
            <RadioGroup
              row
              sx={{
                marginLeft: "8px",
                gap: "40px",
              }}
              aria-labelledby="printDirection"
              name="radio-buttons-group"
              value={printDirection??0}
              onChange={handlePrintDirection}
            >
              <FormControlLabel
                value={0}
                control={<Radio />}
                label="Horizontal"
              />
              <FormControlLabel
                value={1}
                control={<Radio />}
                label="Vertical"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Divider
          sx={{
            margin: "16px 0",
            display: exportModel === EPrintModel.DPAModel ? "none" : "flex",
          }}
        />
        <Box
          id="other3"
          sx={{
            display: exportModel === EPrintModel.DPAModel ? "none" : "flex",
            ...styleObj,
          }}
        >
          <FormControl>
            <FormLabel
              sx={{ marginBottom: "4px" }}
            >{`Height of model base(1~20mm)`}</FormLabel>
            <TextField
              sx={{
                width: "100px",
                "& .MuiInputBase-input": {
                  padding: "8px 12px",
                  height: "24px",
                },
              }}
              value={heightModel??""}
              onChange={(e) => {
                handleInput(e, 1, 20, setHeightModel)
              }}
              onBlur={handleBlurHeight}
            />
          </FormControl>
        </Box>
        <Box
          id="other4"
          sx={{
            display: exportModel !== EPrintModel.DPAModel ? "none" : "flex",

            ...styleObj,
            height: "auto",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <UText
              sx={{
                color: "rgba(0,0,0,0.7)",
                fontSiz: "16px",

                fontWeight: "500",
              }}
            >{`DPA (Direct Print Aligner)`}</UText>
            <Box
              sx={{
                display: "flex",
                gap: "24px",
                width: "100%",
                marginTop: "8px",
              }}
            >
              <FormControl
                sx={{
                  height: "80px",
                  display: "flex",
                  flex: 1,
                }}
              >
                <FormLabel
                  sx={{ marginBottom: "4px" }}
                >{`Offset(-0.4~0.4mm)`}</FormLabel>

                <TextField
                  sx={{
                    width: "100px",
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      height: "24px",
                    },
                  }}
                  value={offesetDPA??""}
                  onChange={(e) => {
                    handleOffesetChange(e)
                  }}
                  onBlur={handleBlurOffeset}
                />
              </FormControl>
              <FormControl sx={{ display: "flex", height: "80px", flex: 1 }}>
                <FormLabel
                  sx={{ marginBottom: "4px" }}
                >{`Thickness(0.31~1.5mm)`}</FormLabel>
                <TextField
                  sx={{
                    width: "100px",
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      height: "24px",
                    },
                  }}
                  value={thicknessDPA??""}
                  onChange={(e) => {
                    handleThicknessChange(e)
                  }}
                  onBlur={handleBlurThickness}
                />
              </FormControl>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
